// src/utils/invoicing/index.js
//
// Public API — the only import OrganizationDeliveries (or any UI) needs.
//
// Usage:
//   import { generateOutgoingInvoices, generatePurchaseInvoices } from '../../../utils/invoicing';
//
//   await generateOutgoingInvoices({
//     orgInfo:    orgData.paymentInfo,   // seller details
//     deliveries: completedDeliveries,
//     senders:    orgData.members,
//     format:     'pdf',                 // 'pdf' | 'finvoice'
//   });

import { buildOutgoingGroups, buildPurchaseGroups } from './invoiceBuilder';
import { renderOutgoingPdf, renderPurchasePdf }     from './pdfRenderer';
import { renderFinvoice }                            from './finvoiceRenderer';

// ─── Outgoing invoices (org → senders) ───────────────────────────────────────

/**
 * @param {object}   opts.orgInfo    - seller / payment info
 * @param {object[]} opts.deliveries - completed deliveries to invoice
 * @param {object[]} opts.senders    - member documents
 * @param {'pdf'|'finvoice'} opts.format
 * @returns {{ invoiced: string[], skipped: Array<{buyer, reason}> }}
 */
export function generateOutgoingInvoices({ orgInfo, deliveries, senders, format = 'pdf' }) {
  const alvRate = orgInfo?.alvRate ?? 25.5;
  const groups  = buildOutgoingGroups(deliveries, senders, alvRate);

  const invoiced = [];
  const skipped  = [];

  for (const g of groups) {
    if (g.skipped) {
      skipped.push({ buyer: g.buyer, reason: g.reason });
      continue;
    }

    if (format === 'finvoice') {
      renderFinvoice(orgInfo, g.invoiceData, 'outgoing');
    } else {
      renderOutgoingPdf(orgInfo, g.invoiceData);
    }

    invoiced.push(g.invoiceData.invoiceNumber);
  }

  return { invoiced, skipped };
}

// ─── Purchase invoices (org → couriers) ──────────────────────────────────────

/**
 * @param {object}   opts.orgInfo    - org billing info (shown as the paying party)
 * @param {object[]} opts.deliveries - completed deliveries to pay out
 * @param {object[]} opts.couriers   - member documents
 * @param {'pdf'|'finvoice'} opts.format
 * @returns {{ invoiced: string[], skipped: Array<{payee, reason}> }}
 */
export function generatePurchaseInvoices({ orgInfo, deliveries, couriers, format = 'pdf' }) {
  const groups = buildPurchaseGroups(deliveries, couriers);

  const invoiced = [];
  const skipped  = [];

  for (const g of groups) {
    if (g.skipped) {
      skipped.push({ payee: g.payee, reason: g.reason });
      continue;
    }

    if (format === 'finvoice') {
      renderFinvoice(orgInfo, g.invoiceData, 'purchase');
    } else {
      renderPurchasePdf(orgInfo, g.invoiceData);
    }

    invoiced.push(g.invoiceData.invoiceNumber);
  }

  return { invoiced, skipped };
}
